import * as z from "zod";

export const BoxStatusEnum = z.enum(["active", "inactive", "draft"]);

export const BoxSchema = z.object({
  id: z.string().uuid(),
  name: z
    .string()
    .min(1, "Name is required")
    .max(255, "Name must be less than 255 characters")
    .trim(),
  description: z
    .string()
    .max(255, "Description must be less than 255 characters")
    .nullable()
    .optional(),
  // Flat dimensions - all in mm with 3 decimal places
  flat_width: z
    .string()
    .transform((val) => {
      if (val === "" || val === null || val === undefined) return null;
      const num = parseFloat(val);
      if (isNaN(num)) throw new Error("Invalid number");
      return num.toString();
    })
    .refine(
      (val) => val === null || parseFloat(val) >= 0,
      "Flat width must be positive (mm)"
    )
    .nullable()
    .optional(),
  flat_height: z
    .string()
    .transform((val) => {
      if (val === "" || val === null || val === undefined) return null;
      const num = parseFloat(val);
      if (isNaN(num)) throw new Error("Invalid number");
      return num.toString();
    })
    .refine(
      (val) => val === null || parseFloat(val) >= 0,
      "Flat height must be positive (mm)"
    )
    .nullable()
    .optional(),
  flat_length: z
    .string()
    .transform((val) => {
      if (val === "" || val === null || val === undefined) return null;
      const num = parseFloat(val);
      if (isNaN(num)) throw new Error("Invalid number");
      return num.toString();
    })
    .refine(
      (val) => val === null || parseFloat(val) >= 0,
      "Flat length must be positive (mm)"
    )
    .nullable()
    .optional(),
  // Assembled dimensions - all in mm with 3 decimal places
  assembled_width: z
    .string()
    .transform((val) => {
      if (val === "" || val === null || val === undefined) return null;
      const num = parseFloat(val);
      if (isNaN(num)) throw new Error("Invalid number");
      return num.toString();
    })
    .refine(
      (val) => val === null || parseFloat(val) >= 0,
      "Assembled width must be positive (mm)"
    )
    .nullable()
    .optional(),
  assembled_height: z
    .string()
    .transform((val) => {
      if (val === null || val === undefined) return null;
      const num = parseFloat(val);
      if (isNaN(num)) throw new Error("Invalid number");
      return num.toString();
    })
    .refine(
      (val) => val === null || parseFloat(val) >= 0,
      "Assembled height must be positive (mm)"
    )
    .nullable()
    .optional(),
  assembled_length: z
    .string()
    .transform((val) => {
      if (val === "" || val === null || val === undefined) return null;
      const num = parseFloat(val);
      if (isNaN(num)) throw new Error("Invalid number");
      return num.toString();
    })
    .refine(
      (val) => val === null || parseFloat(val) >= 0,
      "Assembled length must be positive (mm)"
    )
    .nullable()
    .optional(),
  // Weight in kg with 3 decimal places
  weight: z
    .string()
    .transform((val) => {
      if (val === "" || val === null || val === undefined) return null;
      const num = parseFloat(val);
      if (isNaN(num)) throw new Error("Invalid number");
      return num.toString();
    })
    .refine(
      (val) => val === null || parseFloat(val) >= 0,
      "Weight must be positive (kg)"
    )
    .nullable()
    .optional(),
  material_id: z
    .string()
    .uuid("Must be a valid material ID")
    .nullable()
    .optional(),
  images: z.array(z.string().url("Must be a valid URL")).nullable().optional(),
  design_files: z
    .array(z.string().url("Must be a valid URL"))
    .nullable()
    .optional(),
  status: BoxStatusEnum.default("draft"),
  created: z
    .date()
    .optional()
    .default(() => new Date()),
  updated: z
    .date()
    .optional()
    .default(() => new Date()),
});

export const createBoxSchema = BoxSchema.omit({
  id: true,
  created: true,
  updated: true,
});

export const updateBoxSchema = BoxSchema.partial().omit({
  id: true,
  created: true,
  updated: true,
});

// Type exports
export type CreateBoxInput = z.infer<typeof createBoxSchema>;
export type UpdateBoxInput = z.infer<typeof updateBoxSchema>;
